"use client";

import { useMemo, useState } from "react";
import {
    Avancer,
    Curseur,
    Ecran,
    Montant,
    Question,
    Recommencer,
    Resultat,
    euros,
    pourcent,
    useParcours,
} from "./Journey";
import { revenuNet, SEUIL_TAXE_CHAMBRE } from "@/lib/tools/microEnterprise";
import { expliquerMicro } from "@/lib/tools/explanations";
import { IndiceExplication, MontantExplicable, PanneauExplication, useExplications } from "./Explanation";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";
import { reprendre } from "@/lib/tools/dossier";

/**
 * Combien vous reste-t-il vraiment sur ce que vous encaissez ?
 *
 * QUATRE PRÉLÈVEMENTS, ET LA PLUPART DES CALCULATEURS N'EN COMPTENT QU'UN.
 * Les cotisations sociales, tout le monde les connaît. La contribution à la
 * formation professionnelle s'y ajoute sur le même chiffre d'affaires et
 * personne ne l'affiche, alors qu'elle triple entre un commerçant et un
 * artisan. Les taxes de chambre ne sont pas chiffrées ici, faute de taux
 * confirmés à la source, mais elles sont nommées : un net trop beau vaut moins
 * qu'un net honnête. Et l'impôt sur le revenu dépend du foyer entier.
 *
 * L'IMPÔT AFFICHÉ EST LE COÛT MARGINAL DE L'ACTIVITÉ, ce que le foyer paie en
 * plus parce que cette activité existe. C'est la seule façon d'obtenir un « ce
 * qu'il me reste » interprétable quand il y a un salaire à côté : compter
 * l'impôt du foyer entier ferait apparaître une micro-entreprise ruineuse dès
 * que le conjoint est bien payé.
 */

const PHASES = {
    activite: 0,
    artisan: 0,
    couple: 1,
    enfants: 1,
    caseT: 1,
    autres: 1,
    montantAutres: 1,
    option: 2,
    resultat: 2,
};

const L = {
    fr: {
        phases: ["Votre activité", "Votre foyer", "Votre impôt"],
        retour: "Retour",
        continuer: "Continuer",
        recommencer: "Refaire le calcul",

        qActivite: "Quelle est votre activité ?",
        qActiviteAide:
            "Elle fixe votre taux de cotisations et votre abattement, les deux chiffres qui pèsent le plus sur le résultat.",
        vente: "Vente de marchandises",
        venteDetail: "Achat-revente, hôtellerie, restauration",
        services: "Prestations de services",
        servicesDetail: "Relevant des BIC : bâtiment, coiffure, chambres d'hôtes",
        bnc: "Activité libérale",
        bncDetail: "Relevant des BNC : conseil, création, formation",
        cipav: "Profession libérale réglementée",
        cipavDetail: "Architecte, psychologue, ostéopathe, guide-conférencier",

        qArtisan: "Êtes-vous inscrit au répertoire des métiers ?",
        qArtisanAide:
            "Cela ne change pas vos cotisations, mais cela triple votre contribution à la formation professionnelle : 0,3 % au lieu de 0,1 %.",
        artisanOui: "Oui, je suis artisan",
        artisanOuiDetail: "Inscrit à la chambre de métiers et de l'artisanat",
        artisanNon: "Non, je suis commerçant",
        artisanNonDetail: "Inscrit au registre du commerce, ou ni l'un ni l'autre",

        qCouple: "Vivez-vous seul ou en couple ?",
        qCoupleAide:
            "Un couple marié ou pacsé déclare ensemble, ce qui double le nombre de parts et abaisse le taux d'imposition.",
        seul: "Seul",
        seulDetail: "Célibataire, divorcé ou veuf",
        couple: "En couple",
        coupleDetail: "Marié ou pacsé, déclaration commune",

        qEnfants: "Combien d'enfants à charge ?",
        qEnfantsAide:
            "Chaque enfant ajoute une demi-part, une part entière à partir du troisième. L'avantage est plafonné à 1 807 € par demi-part.",
        enfants0: "Aucun",
        enfants1: "Un",
        enfants2: "Deux",
        enfants3: "Trois ou plus",

        qCaseT: "Élevez-vous seul vos enfants ?",
        qCaseTAide:
            "C'est la case T de votre déclaration, et elle pèse plus lourd que les autres : votre premier enfant vaut alors une part entière et non une demi-part, avec un avantage plafonné à 4 262 € au lieu de 1 807 €.",
        caseTOui: "Oui, seul avec eux",
        caseTOuiDetail: "Personne d'autre ne vit à votre foyer",
        caseTNon: "Non",
        caseTNonDetail: "Vous vivez en concubinage, ou l'autre parent est au foyer",

        qAutres: "D'autres revenus dans le foyer ?",
        qAutresAide:
            "Un salaire, une pension, les revenus de votre conjoint. Ils changent la tranche d'imposition, donc ce que votre activité vous coûte réellement.",
        autresNon: "Non, seulement la micro",
        autresNonDetail: "Aucun autre revenu imposable",
        autresOui: "Oui",
        autresOuiDetail: "Salaire, pension, revenus du conjoint",
        qMontantAutres: "Combien, au total, sur l'année ?",
        qMontantAutresAide:
            "Le net imposable du foyer hors micro-entreprise, celui qui figure sur vos déclarations.",
        autresRevenus: "Autres revenus du foyer",

        qOption: "Avez-vous opté pour le versement libératoire ?",
        qOptionAide:
            "L'impôt est alors prélevé en même temps que les cotisations, à taux fixe sur le chiffre d'affaires, au lieu d'être calculé au barème.",
        optionOui: "Oui, j'ai opté",
        optionOuiDetail: "Impôt payé à l'Urssaf, à chaque déclaration",
        optionNon: "Non, je suis au barème",
        optionNonDetail: "C'est le cas par défaut",

        resultatTitre: "Ce qu'il vous reste",
        ca: "Chiffre d'affaires encaissé dans l'année",
        caAide: "Déplacez le curseur : tout se recalcule.",
        net: "Il vous reste",
        netAide: "soit {taux} de votre chiffre d'affaires",
        cotisations: "Cotisations sociales",
        cfp: "Contribution à la formation professionnelle",
        impot: "Impôt sur le revenu",
        impotAide: "Ce que votre foyer paie en plus du fait de cette activité.",
        parMois: "{montant} par mois",

        autreOptionMieux:
            "L'autre option fiscale vous laisserait {net}, soit {ecart} de plus. Pour trancher votre cas, utilisez le comparateur du versement libératoire.",
        autreOptionPire:
            "Vous avez choisi la bonne option : l'autre vous laisserait {net}, soit {ecart} de moins.",
        autreOptionEgal: "Les deux options fiscales donnent ici le même résultat.",

        taxeChambre:
            "À cela s'ajoutent les taxes pour frais de chambre consulaire, non chiffrées ici : leur taux dépend de votre activité et de votre chambre. Elles ne sont dues qu'à partir de la deuxième année d'activité, et pas du tout en dessous de {seuil} de chiffre d'affaires.",
        taxeChambreLiberal:
            "En libéral, vous ne payez pas de taxe de chambre consulaire : elle ne vise que les commerçants et les artisans.",
        note: "Pour ce calcul, nous avons supposé une année pleine sans ACRE. Avec l'ACRE, vos cotisations sont réduites pendant les premiers trimestres, mais ni la contribution formation ni l'impôt ne le sont.",
    },
    en: {
        phases: ["Your activity", "Your household", "Your tax"],
        retour: "Back",
        continuer: "Continue",
        recommencer: "Start over",

        qActivite: "What is your activity?",
        qActiviteAide:
            "It sets your contribution rate and your allowance, the two figures that weigh most on the result.",
        vente: "Sale of goods",
        venteDetail: "Retail, hotel trade, catering",
        services: "Services",
        servicesDetail: "Commercial (BIC): building, hairdressing, guest rooms",
        bnc: "Liberal profession",
        bncDetail: "Non-commercial (BNC): consulting, creative work, training",
        cipav: "Regulated liberal profession",
        cipavDetail: "Architect, psychologist, osteopath, tour guide",

        qArtisan: "Are you on the trades register?",
        qArtisanAide:
            "It does not change your contributions, but it triples your vocational training contribution: 0.3% instead of 0.1%.",
        artisanOui: "Yes, I am a tradesperson",
        artisanOuiDetail: "Registered with the chamber of trades",
        artisanNon: "No, I am a trader",
        artisanNonDetail: "On the trade register, or on neither",

        qCouple: "Do you live alone or with a partner?",
        qCoupleAide:
            "A married or civil-partnered couple files together, which doubles the number of shares and lowers the tax rate.",
        seul: "Alone",
        seulDetail: "Single, divorced or widowed",
        couple: "With a partner",
        coupleDetail: "Married or in a civil partnership, joint return",

        qEnfants: "How many dependent children?",
        qEnfantsAide:
            "Each child adds half a share, a full one from the third. The benefit is capped at €1,807 per half share.",
        enfants0: "None",
        enfants1: "One",
        enfants2: "Two",
        enfants3: "Three or more",

        qCaseT: "Do you raise your children alone?",
        qCaseTAide:
            "This is box T on the French return, and it weighs more than the others: your first child is then worth a full share rather than half a share, with a benefit capped at 4,262 € instead of 1,807 €.",
        caseTOui: "Yes, alone with them",
        caseTOuiDetail: "Nobody else lives in your household",
        caseTNon: "No",
        caseTNonDetail: "You live with a partner, or the other parent is in the household",

        qAutres: "Any other household income?",
        qAutresAide:
            "A salary, a pension, your partner's income. They change the tax band, and therefore what your activity really costs you.",
        autresNon: "No, only the micro",
        autresNonDetail: "No other taxable income",
        autresOui: "Yes",
        autresOuiDetail: "Salary, pension, partner's income",
        qMontantAutres: "How much, in total, over the year?",
        qMontantAutresAide:
            "The household's taxable net excluding the micro-entreprise, as shown on your returns.",
        autresRevenus: "Other household income",

        qOption: "Have you opted for the flat-rate tax payment?",
        qOptionAide:
            "Tax is then collected alongside contributions, at a fixed rate on turnover, instead of being computed on the scale.",
        optionOui: "Yes, I opted in",
        optionOuiDetail: "Tax paid to URSSAF with each return",
        optionNon: "No, I am on the scale",
        optionNonDetail: "That is the default",

        resultatTitre: "What you keep",
        ca: "Turnover collected during the year",
        caAide: "Move the slider: everything recalculates.",
        net: "You keep",
        netAide: "that is {taux} of your turnover",
        cotisations: "Social contributions",
        cfp: "Vocational training contribution",
        impot: "Income tax",
        impotAide: "What your household pays extra because this activity exists.",
        parMois: "{montant} a month",

        autreOptionMieux:
            "The other tax option would leave you {net}, that is {ecart} more. To settle your case, use the flat-rate payment comparator.",
        autreOptionPire:
            "You picked the right option: the other would leave you {net}, that is {ecart} less.",
        autreOptionEgal: "Both tax options give the same result here.",

        taxeChambre:
            "On top of this come the chamber levies, not quantified here: their rate depends on your activity and your chamber. They are only due from the second year of activity, and not at all below {seuil} of turnover.",
        taxeChambreLiberal:
            "In a liberal profession you pay no chamber levy: it only applies to traders and tradespeople.",
        note: "For this calculation, we have assumed a full year without ACRE. With ACRE your contributions are reduced for the first quarters, but neither the training contribution nor the tax is.",
    },
};

/** La part de chaque prélèvement, en une barre empilée. */
function Repartition({ r, ca, t }) {
    if (ca <= 0) return null;
    const parts = [
        { cle: "net", valeur: r.net, couleur: "bg-green-600 dark:bg-green-500" },
        { cle: "cotisations", valeur: r.cotisations, couleur: "bg-primary" },
        { cle: "impot", valeur: r.impot, couleur: "bg-amber-500" },
        { cle: "cfp", valeur: r.cfp, couleur: "bg-gray-400 dark:bg-lightBlue/40" },
    ].filter((p) => p.valeur > 0);

    return (
        // Décorative : la cascade en dessous donne les mêmes parts en toutes
        // lettres, qu'un lecteur d'écran lit déjà.
        <div aria-hidden="true" className="flex gap-0.5 h-3 mt-5 rounded-full overflow-hidden">
            {parts.map(({ cle, valeur, couleur }) => (
                <div
                    key={cle}
                    className={`${couleur} transition-all duration-300`}
                    style={{ width: `${Math.max(0, (valeur / ca) * 100)}%` }}
                    title={t[cle] || t.net}
                />
            ))}
        </div>
    );
}

/** Une ligne de la cascade : un libellé, une pastille de couleur, un montant. */
function Ligne({ libelle, aide, montant, couleur, lang, reduit, explication, ouvert, onBasculer }) {
    return (
        <div className="py-2.5 border-b border-gray-200/70 dark:border-white/10 last:border-0">
            <div className="flex justify-between items-start gap-4">
                <div className="flex items-start gap-2.5 min-w-0">
                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 mt-1.5 ${couleur}`} />
                    <div className="min-w-0">
                        <div className="text-sm dark:text-white">{libelle}</div>
                        {aide && (
                            <div className="text-xs text-gray-500 dark:text-lightBlue/70 mt-0.5">
                                {aide}
                            </div>
                        )}
                    </div>
                </div>
                <div className="text-sm font-semibold tabular-nums dark:text-white shrink-0">
                    −{" "}
                    <MontantExplicable explication={explication} ouvert={ouvert} onBasculer={onBasculer}>
                        <Montant valeur={montant} lang={lang} reduit={reduit} />
                    </MontantExplicable>
                </div>
            </div>
            {/* Le calcul se déplie sous la ligne, dans le fil de la lecture. */}
            {ouvert && <PanneauExplication explication={explication} lang={lang} />}
        </div>
    );
}

export default function SimulateurRevenuNet({ lang = "fr" }) {
    const t = L[lang] || L.fr;

    // Le dossier du lecteur, s'il en a un : les réponses qu'il connaît
    // partent déjà posées, et le parcours s'ouvre sur la première qui manque.
    const { valeurs: dossier } = useDossierOutil();
    const [activite, setActivite] = useState(dossier.activite ?? null);
    const [artisan, setArtisan] = useState(dossier.artisan ?? false);
    const [enCouple, setEnCouple] = useState(dossier.enCouple ?? false);
    const [enfants, setEnfants] = useState(dossier.enfants ?? 0);
    const [parentIsole, setParentIsole] = useState(false);
    const [autresRevenus, setAutresRevenus] = useState(dossier.autresRevenus ?? 0);
    const [optionFiscale, setOptionFiscale] = useState(dossier.optionFiscale ?? "bareme");
    const [ca, setCa] = useState(dossier.ca ?? 40000);

    const { etape, sens, reduit, aller, retour, recommencer, communs } = useParcours(
        "activite",
        () => {
            setActivite(null);
            setArtisan(false);
            setEnCouple(false);
            setEnfants(0);
            setParentIsole(false);
            setAutresRevenus(0);
            setOptionFiscale("bareme");
        },
        PHASES,
        reprendre([
            { etape: "activite", connu: dossier.activite !== undefined },
            ...(dossier.activite === "vente" || dossier.activite === "services"
                ? [{ etape: "artisan", connu: dossier.artisan !== undefined }]
                : []),
            { etape: "couple", connu: dossier.enCouple !== undefined },
            { etape: "enfants", connu: dossier.enfants !== undefined },
            // La case T ne se déduit pas du dossier : le parcours s'y arrête.
            ...(!dossier.enCouple && dossier.enfants >= 1 ? [{ etape: "caseT", connu: false }] : []),
            { etape: "autres", connu: dossier.autresRevenus !== undefined },
            ...(dossier.autresRevenus > 0 ? [{ etape: "montantAutres", connu: true }] : []),
            { etape: "option", connu: dossier.optionFiscale !== undefined },
            { etape: "resultat" },
        ]),
    );

    useSignalerDossier(
        { activite, artisan, enCouple, enfants, autresRevenus, optionFiscale, ca },
        etape === "resultat",
    );

    const r = useMemo(
        () =>
            activite
                ? revenuNet({
                      chiffreAffaires: ca,
                      activite,
                      artisan,
                      optionFiscale,
                      autresRevenus,
                      enCouple,
                      enfants,
                      parentIsole,
                  })
                : null,
        [ca, activite, artisan, optionFiscale, autresRevenus, enCouple, enfants, parentIsole],
    );

    // Chaque montant du résultat dit d'où il vient : ses étapes et ses sources.
    const ex = useMemo(
        () =>
            activite
                ? expliquerMicro(
                      { chiffreAffaires: ca, activite, artisan, optionFiscale, autresRevenus, enCouple, enfants, parentIsole },
                      lang,
                  )
                : null,
        [ca, activite, artisan, optionFiscale, autresRevenus, enCouple, enfants, parentIsole, lang],
    );
    const { ouverte, basculer } = useExplications();

    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };

    if (etape === "activite") {
        return (
            <Question
                key={etape}
                {...communs}
                phases={t.phases}
                retour={t.retour}
                phase={PHASES.activite}
                titre={t.qActivite}
                aide={t.qActiviteAide}
                choix={["vente", "services", "bnc", "cipav"].map((cle) => ({
                    icone: {
                        vente: "sale-linear",
                        services: "pen-2-linear",
                        bnc: "brain-linear",
                        cipav: "medal-ribbon-star-linear",
                    }[cle],
                    titre: t[cle],
                    detail: t[`${cle}Detail`],
                    onClick: () => {
                        setActivite(cle);
                        // La contribution formation d'un libéral ne dépend pas
                        // du répertoire des métiers : la question n'a de sens
                        // que pour la vente et les prestations.
                        aller(cle === "bnc" || cle === "cipav" ? "couple" : "artisan");
                    },
                }))}
            />
        );
    }

    if (etape === "artisan") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.artisan}
                titre={t.qArtisan}
                aide={t.qArtisanAide}
                choix={[
                    {
                        icone: "clipboard-check-linear",
                        titre: t.artisanOui,
                        detail: t.artisanOuiDetail,
                        onClick: () => {
                            setArtisan(true);
                            aller("couple");
                        },
                    },
                    {
                        icone: "sale-linear",
                        titre: t.artisanNon,
                        detail: t.artisanNonDetail,
                        onClick: () => {
                            setArtisan(false);
                            aller("couple");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "couple") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.couple}
                titre={t.qCouple}
                aide={t.qCoupleAide}
                choix={[
                    {
                        icone: "user-rounded-linear",
                        titre: t.seul,
                        detail: t.seulDetail,
                        onClick: () => {
                            setEnCouple(false);
                            aller("enfants");
                        },
                    },
                    {
                        icone: "users-group-rounded-linear",
                        titre: t.couple,
                        detail: t.coupleDetail,
                        onClick: () => {
                            setEnCouple(true);
                            aller("enfants");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "enfants") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.enfants}
                titre={t.qEnfants}
                aide={t.qEnfantsAide}
                choix={[0, 1, 2, 3].map((n) => ({
                    icone: n === 0 ? "close-circle-linear" : "user-plus-linear",
                    titre: t[`enfants${n}`],
                    onClick: () => {
                        setEnfants(n);
                        // La case T n'existe que pour une personne seule ayant
                        // au moins un enfant.
                        if (!enCouple && n >= 1) {
                            aller("caseT");
                            return;
                        }
                        setParentIsole(false);
                        aller("autres");
                    },
                }))}
            />
        );
    }

    if (etape === "caseT") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.caseT}
                titre={t.qCaseT}
                aide={t.qCaseTAide}
                choix={[
                    {
                        icone: "user-hand-up-linear",
                        titre: t.caseTOui,
                        detail: t.caseTOuiDetail,
                        onClick: () => {
                            setParentIsole(true);
                            aller("autres");
                        },
                    },
                    {
                        icone: "users-group-rounded-linear",
                        titre: t.caseTNon,
                        detail: t.caseTNonDetail,
                        onClick: () => {
                            setParentIsole(false);
                            aller("autres");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "autres") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.autres}
                titre={t.qAutres}
                aide={t.qAutresAide}
                choix={[
                    {
                        icone: "close-circle-linear",
                        titre: t.autresNon,
                        detail: t.autresNonDetail,
                        onClick: () => {
                            setAutresRevenus(0);
                            aller("option");
                        },
                    },
                    {
                        icone: "wallet-money-linear",
                        titre: t.autresOui,
                        detail: t.autresOuiDetail,
                        onClick: () => {
                            setAutresRevenus(30000);
                            aller("montantAutres");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "montantAutres") {
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES.montantAutres}
                titre={t.qMontantAutres}
                aide={t.qMontantAutresAide}
            >
                <Curseur
                    libelle={t.autresRevenus}
                    valeur={autresRevenus}
                    poser={setAutresRevenus}
                    maximum={120000}
                    pas={1000}
                    formater={(v) => euros(v, lang)}
                />
                <Avancer onClick={() => aller("option")}>{t.continuer}</Avancer>
            </Ecran>
        );
    }

    if (etape === "option") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.option}
                titre={t.qOption}
                aide={t.qOptionAide}
                choix={[
                    {
                        icone: "close-circle-linear",
                        titre: t.optionNon,
                        detail: t.optionNonDetail,
                        onClick: () => {
                            setOptionFiscale("bareme");
                            aller("resultat");
                        },
                    },
                    {
                        icone: "check-circle-linear",
                        titre: t.optionOui,
                        detail: t.optionOuiDetail,
                        onClick: () => {
                            setOptionFiscale("vfl");
                            aller("resultat");
                        },
                    },
                ]}
            />
        );
    }

    const ecart = r.netAutreOption - r.net;

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            <div className="mt-4">
                <Curseur
                    libelle={t.ca}
                    aide={t.caAide}
                    valeur={ca}
                    poser={setCa}
                    maximum={activite === "vente" ? 203100 : 83600}
                    pas={500}
                    formater={(v) => euros(v, lang)}
                />
            </div>

            <Repartition r={r} ca={ca} t={t} />

            <IndiceExplication lang={lang} />

            <div className="mt-3">
                <Ligne
                    libelle={t.cotisations}
                    montant={r.cotisations}
                    reduit={reduit}
                    couleur="bg-primary"
                    lang={lang}
                    explication={ex.cotisations}
                    ouvert={ouverte === "cotisations"}
                    onBasculer={() => basculer("cotisations")}
                />
                <Ligne
                    libelle={t.impot}
                    aide={t.impotAide}
                    montant={r.impot}
                    reduit={reduit}
                    couleur="bg-amber-500"
                    lang={lang}
                    explication={ex.impot}
                    ouvert={ouverte === "impot"}
                    onBasculer={() => basculer("impot")}
                />
                <Ligne
                    libelle={t.cfp}
                    montant={r.cfp}
                    reduit={reduit}
                    couleur="bg-gray-400 dark:bg-lightBlue/40"
                    lang={lang}
                    explication={ex.cfp}
                    ouvert={ouverte === "cfp"}
                    onBasculer={() => basculer("cfp")}
                />
            </div>

            <div className="mt-6 rounded-xl p-5 border border-green-600/40 bg-green-600/[0.06]">
                <div className="text-xs text-gray-500 dark:text-lightBlue/70 mb-1">
                    {t.net}
                </div>
                <div className="text-3xl font-bold tabular-nums text-green-700 dark:text-green-400">
                    <MontantExplicable explication={ex.net} ouvert={ouverte === "net"} onBasculer={() => basculer("net")}>
                        <Montant valeur={r.net} lang={lang} reduit={reduit} />
                    </MontantExplicable>
                </div>
                <div className="text-sm text-gray-600 dark:text-lightBlue mt-1">
                    {t.netAide.replace(
                        "{taux}",
                        pourcent(1 - r.tauxPrelevement, lang, 0),
                    )}{" "}
                    ·{" "}
                    {t.parMois.replace("{montant}", euros(r.net / 12, lang))}
                </div>
                {ouverte === "net" && <PanneauExplication explication={ex.net} lang={lang} />}
            </div>

            <p className="text-sm text-gray-600 dark:text-lightBlue mt-5 mb-0">
                {Math.abs(ecart) < 1
                    ? t.autreOptionEgal
                    : (ecart > 0 ? t.autreOptionMieux : t.autreOptionPire)
                          .replace("{net}", euros(r.netAutreOption, lang))
                          .replace("{ecart}", euros(Math.abs(ecart), lang))}
            </p>

            {/* Les taxes de chambre ne visent que les commerçants et les
                artisans : un libéral n'en paie pas, et le lui dire vaut mieux
                que de l'inquiéter pour rien. */}
            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">
                {activite === "bnc" || activite === "cipav"
                    ? t.taxeChambreLiberal
                    : t.taxeChambre.replace("{seuil}", euros(SEUIL_TAXE_CHAMBRE, lang))}
            </p>
            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-2 mb-0">{t.note}</p>

            <Recommencer onClick={recommencer} libelle={t.recommencer} />
        </Resultat>
    );
}
